import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import type { TelegramGroupPortfolio, TelegramPortfolio, TelegramUser } from '../../utils/telegramAuth';
import { fetchTelegramPortfolio } from '../../utils/telegramAuth';
import { getSolanaTokenByAddress, getSentryGroupPhotos } from '../../utils/supabase';

type UserGroupPortfolioProps = {
  telegramUser: TelegramUser | null;
  headerAction?: React.ReactNode;
  statusMessage?: string | null;
  className?: string;
};

const PortfolioCard = styled.div`
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
`;

const PortfolioHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

const PortfolioHeaderMain = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const PortfolioHeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  position: relative;
  z-index: 2;
`;

const PortfolioTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const PortfolioTotal = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #10b981;
`;

const GroupList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GroupCard = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 16px;
  background: rgba(10, 10, 15, 0.6);
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const GroupIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const GroupPhoto = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  border: 1px solid rgba(255, 255, 255, 0.12);
  flex-shrink: 0;
`;

const GroupPhotoFallback = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
`;

const GroupName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
`;

const GroupMeta = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    max-width: 180px;
  }
`;

const GroupIdentityText = styled.div`
  min-width: 0;
`;

const GroupTotals = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const GroupTotalItem = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);

  span {
    color: #10b981;
    font-weight: 600;
    margin-left: 6px;
  }
`;

const HoldingsToggleRow = styled.button`
  margin-top: 12px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  cursor: pointer;
`;

const HoldingsLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const HoldingsChevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 0.2s ease;
`;

const PositionList = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PositionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
`;

const PositionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const PositionLogo = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
`;

const PositionLogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PositionLogoFallback = styled.span`
  font-size: 9px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
`;

const PositionName = styled.div`
  font-size: 12px;
  color: #ffffff;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PositionStats = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-align: right;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PositionAmount = styled.span`
  color: rgba(255, 255, 255, 0.65);
  font-size: 11px;
`;

const PositionUsd = styled.span`
  color: #10b981;
  font-weight: 600;
`;

const EmptyState = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  padding: 12px 0;
`;

const formatUsd = (value: number): string =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatSol = (value: number): string =>
  `${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SOL`;

const formatTokenAmount = (value: number): string =>
  value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const normalizeLogoUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('ipfs://')) {
    return url.replace('ipfs://', 'https://ipfs.io/ipfs/');
  }
  return url;
};

const normalizeAddressKey = (address?: string | null): string | null => {
  if (!address) return null;
  if (address.startsWith('0x') || address.startsWith('0X')) {
    return address.toLowerCase();
  }
  return address;
};

const fetchDexscreenerLogo = async (address: string): Promise<string | null> => {
  try {
    const response = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${address}`);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    const pair = Array.isArray(data?.pairs) ? data.pairs[0] : null;
    const logo = pair?.baseToken?.logoURI || pair?.info?.imageUrl || null;
    return normalizeLogoUrl(logo);
  } catch (error) {
    return null;
  }
};

const fetchDexscreenerLogoBySymbol = async (symbol: string): Promise<string | null> => {
  try {
    const response = await fetch(`https://api.dexscreener.com/latest/dex/search/?q=${encodeURIComponent(symbol)}`);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    const pairs = Array.isArray(data?.pairs) ? data.pairs : [];
    const match = pairs.find((pair: any) => {
      const chainId = (pair?.chainId || '').toLowerCase();
      const baseSymbol = (pair?.baseToken?.symbol || '').toLowerCase();
      return chainId === 'solana' && baseSymbol === symbol.toLowerCase();
    }) || pairs.find((pair: any) => (pair?.chainId || '').toLowerCase() === 'solana');
    const logo = match?.baseToken?.logoURI || match?.info?.imageUrl || null;
    return normalizeLogoUrl(logo);
  } catch (error) {
    return null;
  }
};

const normalizePortfolio = (portfolio: TelegramPortfolio | null): TelegramPortfolio => {
  if (!portfolio) return { totalUsdValue: 0, groups: [] };
  return {
    totalUsdValue: Number(portfolio.totalUsdValue || 0),
    groups: Array.isArray(portfolio.groups) ? portfolio.groups : []
  };
};

const UserGroupPortfolio: React.FC<UserGroupPortfolioProps> = ({
  telegramUser,
  headerAction,
  statusMessage,
  className
}) => {
  const [portfolio, setPortfolio] = useState<TelegramPortfolio | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<Set<number>>(new Set());
  const [tokenLogos, setTokenLogos] = useState<Record<string, string | null>>({});
  const [groupPhotos, setGroupPhotos] = useState<Map<number, string | null>>(new Map());
  const [symbolLogos, setSymbolLogos] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (!telegramUser) {
      setPortfolio(null);
      setError(null);
      return;
    }

    let cancelled = false;
    const loadPortfolio = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchTelegramPortfolio();
        if (!cancelled) {
          setPortfolio(data);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'Failed to load portfolio.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPortfolio();

    return () => {
      cancelled = true;
    };
  }, [telegramUser]);

  const normalized = useMemo(() => normalizePortfolio(portfolio), [portfolio]);
  const groups: TelegramGroupPortfolio[] = normalized.groups;
  const computedTotalUsd = groups.reduce((sum, group) => sum + Number(group.totalUsdValue || 0), 0);
  const totalUsd = normalized.totalUsdValue || computedTotalUsd;

  useEffect(() => {
    if (!telegramUser || groups.length === 0) return;

    let cancelled = false;
    const loadLogos = async () => {
      const addresses = new Set<string>();
      const symbols = new Set<string>();
      groups.forEach((group) => {
        group.positions?.forEach((position) => {
          if (position.tokenAddress) {
            const key = normalizeAddressKey(position.tokenAddress);
            if (key) {
              addresses.add(key);
            }
          }
          if (position.symbol) {
            symbols.add(position.symbol);
          }
        });
      });

      const missing = Array.from(addresses).filter((address) => !(address in tokenLogos));
      if (missing.length === 0) return;

      const supabaseEntries = await Promise.all(
        missing.map(async (address) => {
          const token = await getSolanaTokenByAddress(address);
          return [address, normalizeLogoUrl(token?.logo_url || null)] as const;
        })
      );

      if (!cancelled) {
        setTokenLogos((prev) => {
          const next = { ...prev };
          supabaseEntries.forEach(([address, logoUrl]) => {
            next[address] = logoUrl;
          });
          return next;
        });
      }

      const unresolved = supabaseEntries.filter(([, logoUrl]) => !logoUrl).map(([address]) => address);
      if (unresolved.length === 0) return;

      let relayEntries = unresolved.map((address) => [address, null]) as Array<[string, string | null]>;
      try {
        const relayResponse = await fetch('https://api.relay.link/currencies/v2', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            chainIds: [792703809],
            tokens: unresolved,
            includeAllChains: true,
            useExternalSearch: true,
            verified: false,
            limit: unresolved.length
          })
        });

        if (relayResponse.ok) {
          const relayPayload = await relayResponse.json();
          const relayCurrencies = Array.isArray(relayPayload)
            ? relayPayload
            : Array.isArray(relayPayload?.currencies)
              ? relayPayload.currencies
              : Array.isArray(relayPayload?.data)
                ? relayPayload.data
                : Array.isArray(relayPayload?.items)
                  ? relayPayload.items
                  : [];

          const relayMap = new Map(
            relayCurrencies.map((currency: any) => [
              normalizeAddressKey(currency.address),
              normalizeLogoUrl(currency.metadata?.logoURI || null)
            ])
          );

          relayEntries = unresolved.map((address) => [
            address,
            relayMap.get(address) || null
          ]) as Array<[string, string | null]>;
        }
      } catch (err) {
        // Ignore relay logo fetch failures
      }

      const dexEntries = await Promise.all(
        relayEntries
          .filter(([, logoUrl]) => !logoUrl)
          .map(async ([address]) => {
            const logoUrl = await fetchDexscreenerLogo(address);
            return [address, logoUrl] as const;
          })
      );

      if (!cancelled) {
        setTokenLogos((prev) => {
          const next = { ...prev };
          relayEntries.forEach(([address, logoUrl]) => {
            if (!(address in next) || next[address] === null) {
              next[address] = logoUrl;
            }
          });
          dexEntries.forEach(([address, logoUrl]) => {
            if (!(address in next) || next[address] === null) {
              next[address] = logoUrl;
            }
          });
          return next;
        });
      }

      const missingSymbols = Array.from(symbols).filter((symbol) => !(symbol.toLowerCase() in symbolLogos));
      if (missingSymbols.length > 0) {
        const symbolEntries = await Promise.all(
          missingSymbols.map(async (symbol) => {
            const logoUrl = await fetchDexscreenerLogoBySymbol(symbol);
            return [symbol.toLowerCase(), logoUrl] as const;
          })
        );
        if (!cancelled) {
          setSymbolLogos((prev) => {
            const next = { ...prev };
            symbolEntries.forEach(([symbol, logoUrl]) => {
              next[symbol] = logoUrl;
            });
            return next;
          });
        }
      }
    };

    loadLogos();

    return () => {
      cancelled = true;
    };
  }, [groups, telegramUser, tokenLogos, symbolLogos]);

  useEffect(() => {
    if (!telegramUser || groups.length === 0) return;

    let cancelled = false;
    const loadGroupPhotos = async () => {
      const chatIds = groups.map((group) => group.chatId);
      const photoMap = await getSentryGroupPhotos(chatIds);
      if (!cancelled) {
        setGroupPhotos(photoMap);
      }
    };

    loadGroupPhotos();

    return () => {
      cancelled = true;
    };
  }, [groups, telegramUser]);

  const toggleGroup = (chatId: number) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(chatId)) {
        next.delete(chatId);
      } else {
        next.add(chatId);
      }
      return next;
    });
  };

  return (
    <PortfolioCard className={className}>
      <PortfolioHeader>
        <PortfolioHeaderMain>
          <PortfolioTitle>My Groups Portfolio</PortfolioTitle>
          <PortfolioTotal>{formatUsd(totalUsd)}</PortfolioTotal>
        </PortfolioHeaderMain>
        {headerAction && <PortfolioHeaderActions>{headerAction}</PortfolioHeaderActions>}
      </PortfolioHeader>

      {statusMessage && <EmptyState>{statusMessage}</EmptyState>}

      {!telegramUser && (
        <EmptyState>Connect Telegram to view your group portfolio.</EmptyState>
      )}

      {loading && <EmptyState>Loading group balances...</EmptyState>}
      {!loading && error && <EmptyState>{error}</EmptyState>}
      {!loading && !error && telegramUser && groups.length === 0 && (
        <EmptyState>No trading groups linked to this Telegram account yet.</EmptyState>
      )}

      {!loading && !error && telegramUser && groups.length > 0 && (
        <GroupList>
          {groups.map((group) => (
            <GroupCard key={group.chatId}>
              <GroupHeader>
                <GroupIdentity>
                  {groupPhotos.get(group.chatId) ? (
                    <GroupPhoto
                      src={groupPhotos.get(group.chatId) || ''}
                      alt={group.groupName || `Group ${group.chatId}`}
                    />
                  ) : (
                    <GroupPhotoFallback>{(group.groupName || 'G').slice(0, 1)}</GroupPhotoFallback>
                  )}
                  <GroupIdentityText>
                    <GroupName>{group.groupName || `Group ${group.chatId}`}</GroupName>
                    <GroupMeta>{group.walletAddress}</GroupMeta>
                  </GroupIdentityText>
                </GroupIdentity>
                <GroupTotals>
                  <GroupTotalItem>
                    Total
                    <span>{formatUsd(Number(group.totalUsdValue || 0))}</span>
                  </GroupTotalItem>
                  <GroupTotalItem>
                    Wallet
                    <span>{formatSol(Number(group.totalSol || 0))}</span>
                  </GroupTotalItem>
                </GroupTotals>
              </GroupHeader>

              <HoldingsToggleRow onClick={() => toggleGroup(group.chatId)}>
                <HoldingsLabel>Holdings</HoldingsLabel>
                <HoldingsChevron $open={openGroups.has(group.chatId)}>⌄</HoldingsChevron>
              </HoldingsToggleRow>

              {openGroups.has(group.chatId) && (
                <PositionList>
                  {group.positions && group.positions.length > 0 ? (
                    group.positions.map((position, idx) => {
                      const addressKey = normalizeAddressKey(position.tokenAddress);
                      const symbolKey = position.symbol ? position.symbol.toLowerCase() : null;
                      const logoUrl = addressKey
                        ? (tokenLogos[addressKey] || (symbolKey ? symbolLogos[symbolKey] : null))
                        : symbolKey
                          ? symbolLogos[symbolKey]
                          : null;
                      const fallback = position.symbol?.slice(0, 3) || 'TOK';
                      return (
                        <PositionRow key={`${group.chatId}-${position.symbol}-${idx}`}>
                          <PositionLeft>
                            <PositionLogo>
                              {logoUrl ? (
                                <PositionLogoImg src={logoUrl} alt={position.symbol} />
                              ) : (
                                <PositionLogoFallback>{fallback}</PositionLogoFallback>
                              )}
                            </PositionLogo>
                            <PositionName>{position.symbol}</PositionName>
                          </PositionLeft>
                          <PositionStats>
                            <PositionAmount>{formatTokenAmount(position.amount)}</PositionAmount>
                            <PositionUsd>{formatUsd(Number(position.usdValue || 0))}</PositionUsd>
                          </PositionStats>
                        </PositionRow>
                      );
                    })
                  ) : (
                    <EmptyState>No active positions.</EmptyState>
                  )}
                </PositionList>
              )}
            </GroupCard>
          ))}
        </GroupList>
      )}
    </PortfolioCard>
  );
};

export default UserGroupPortfolio;
